/**
 * Wave 27.3 — Gantt swim-lane (Phase E).
 *
 * Two views in one full-page surface:
 *   Tab A — Projects  (Y = projects, X = weeks)
 *   Tab B — Staff     (Y = staff member, X = days/weeks)
 *
 * Bars come from the backend `/insights/gantt/projects` and
 * `/insights/gantt/staff` endpoints (already aggregated).
 *
 * No-modal rule honoured — clicking a bar navigates to the entity's
 * own full-page route (jobcards/{id}, projects/{id}, etc.).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar, Users, Loader2, ChevronLeft, ChevronRight, ArrowLeft,
  AlertTriangle, Inbox, Briefcase,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const TABS = [
  { key: "projects", label: "Projects" },
  { key: "staff", label: "Staff" },
];

// Day in milliseconds, helper formatters.
const ONE_DAY = 86400000;
const fmtShortDate = (d) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
const isoDate = (d) => d.toISOString().slice(0, 10);

export default function GanttView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const allowed = role === "owner" || role === "admin" || role === "manager" || role === "rep";

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "projects");

  // Window — defaults: staff = 5 weeks (today − 1w → today + 4w), projects = 10 weeks.
  const initWeeks = tab === "projects" ? 10 : 5;
  const [weeks, setWeeks] = useState(initWeeks);
  const [originDate, setOriginDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 7);
    return d;
  });

  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);

  const winFrom = useMemo(() => originDate, [originDate]);
  const winTo = useMemo(() => new Date(originDate.getTime() + weeks * 7 * ONE_DAY), [originDate, weeks]);

  const totalDays = weeks * 7;
  // Build a day-by-day axis once
  const days = useMemo(() => {
    const list = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(winFrom.getTime() + i * ONE_DAY);
      list.push(d);
    }
    return list;
  }, [winFrom, totalDays]);

  const todayPct = useMemo(() => {
    const today = Date.now();
    const start = winFrom.getTime();
    const end = winTo.getTime();
    if (today < start || today > end) return null;
    return ((today - start) / (end - start)) * 100;
  }, [winFrom, winTo]);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/insights/gantt/${tab}`, {
        params: { from_date: isoDate(winFrom), to_date: isoDate(winTo) },
      });
      setLanes(data.lanes || []);
    } catch {
      toast.error("Gantt unavailable.");
      setLanes([]);
    } finally { setLoading(false); }
  }, [tab, winFrom, winTo, allowed]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setSearchParams({ tab }, { replace: true });
  }, [tab, setSearchParams]);

  if (!allowed) return (
    <div className="hv-card p-8 text-center text-[#a0aabf] max-w-md mx-auto">
      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
      <div>Gantt timeline is for managers and above.</div>
    </div>
  );

  const shift = (deltaWeeks) => {
    const d = new Date(originDate.getTime() + deltaWeeks * 7 * ONE_DAY);
    setOriginDate(d);
  };

  return (
    <div className="space-y-4" data-testid="gantt-page">
      {/* Header */}
      <div className="flex items-start gap-2 flex-wrap">
        <button onClick={() => navigate(-1)} className="hv-btn hv-btn-ghost text-xs shrink-0" data-testid="gantt-back">
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#1FB6C1]" /> Timeline
          </h1>
          <p className="text-xs text-[#647087] mt-0.5">
            Drag-free swim-lane view of every project and every staff member's load. Click any bar to open the underlying record.
          </p>
        </div>
      </div>

      {/* Tabs + window controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => { setTab(t.key); setWeeks(t.key === "projects" ? 10 : 5); }}
              className={`text-[12px] px-3 py-1.5 rounded-full inline-flex items-center gap-1 transition-colors border ${
                tab === t.key
                  ? "bg-[#1FB6C1] text-[#0a0b0e] border-transparent font-semibold"
                  : "border-white/15 text-[#a0aabf] hover:bg-white/5"
              }`}
              data-testid={`gantt-tab-${t.key}`}
            >
              {t.key === "projects" ? <Briefcase className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-2)} className="hv-btn hv-btn-ghost text-xs" data-testid="gantt-prev" aria-label="Previous 2 weeks">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs text-[#a0aabf] font-mono px-2">
            {fmtShortDate(winFrom)} → {fmtShortDate(winTo)}
          </span>
          <button onClick={() => shift(2)} className="hv-btn hv-btn-ghost text-xs" data-testid="gantt-next" aria-label="Next 2 weeks">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => {
              const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - 7);
              setOriginDate(d);
            }}
            className="hv-btn hv-btn-ghost text-xs ml-1"
            data-testid="gantt-today"
          >
            Today
          </button>
        </div>
      </div>

      {/* Swim-lane grid */}
      <div className="hv-card !p-0 overflow-x-auto" data-testid="gantt-grid">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1FB6C1]" /></div>
        ) : lanes.length === 0 ? (
          <div className="p-10 text-center text-[#647087] text-sm" data-testid="gantt-empty">
            <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <div>Nothing scheduled in this window.</div>
            <div className="text-xs mt-1">Try shifting the window with the arrows above.</div>
          </div>
        ) : (
          <div className="min-w-[800px]">
            {/* Day axis header */}
            <div className="grid sticky top-0 z-10 bg-[#0e1015] border-b border-white/10"
              style={{ gridTemplateColumns: `200px repeat(${totalDays}, minmax(28px, 1fr))` }}
            >
              <div className="p-2 text-[10px] uppercase tracking-wider text-[#647087]">{tab === "projects" ? "Project" : "Staff"}</div>
              {days.map((d, i) => {
                const isWeekStart = d.getDay() === 1;   // Monday
                const isToday = isoDate(d) === isoDate(new Date());
                return (
                  <div
                    key={i}
                    className={`text-center text-[9px] py-1 border-l ${isWeekStart ? "border-white/10" : "border-white/[0.03]"} ${isToday ? "bg-[#1FB6C1]/[0.08]" : ""}`}
                  >
                    <div className="text-[#647087]">{d.toLocaleDateString(undefined, { weekday: "short" })[0]}</div>
                    <div className={isToday ? "text-[#1FB6C1] font-semibold" : "text-[#a0aabf]"}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>

            {/* Lanes */}
            {lanes.map((lane) => (
              <GanttLane
                key={lane.id}
                lane={lane}
                tab={tab}
                winFromMs={winFrom.getTime()}
                winToMs={winTo.getTime()}
                totalDays={totalDays}
              />
            ))}
          </div>
        )}
        {todayPct != null && !loading && lanes.length > 0 && (
          <div
            className="absolute pointer-events-none border-l-2 border-dashed border-[#1FB6C1]/60"
            style={{
              left: `calc(200px + (100% - 200px) * ${todayPct / 100})`,
              top: 0,
              bottom: 0,
            }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

function GanttLane({ lane, tab, winFromMs, winToMs, totalDays }) {
  const spanMs = winToMs - winFromMs;
  return (
    <div
      className="grid border-b border-white/5 hover:bg-white/[0.02]"
      style={{ gridTemplateColumns: `200px repeat(${totalDays}, minmax(28px, 1fr))` }}
      data-testid={`gantt-lane-${lane.id}`}
    >
      {/* Lane label */}
      <div className="p-2 text-xs border-r border-white/5 truncate">
        {tab === "projects" ? (
          <Link to={`/projects/${lane.id}`} className="block hover:text-[#1FB6C1]">
            <div className="font-mono text-[10px] text-[#647087]">{lane.short_id}</div>
            <div className="text-white truncate">{lane.title}</div>
          </Link>
        ) : (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">{lane.role}</div>
            <div className="text-white truncate">{lane.name}</div>
          </div>
        )}
      </div>

      {/* Bars */}
      <div className="relative col-span-full" style={{ gridColumn: `2 / span ${totalDays}`, minHeight: 36 }}>
        {lane.bars.map((bar, i) => {
          const start = bar.start ? new Date(bar.start).getTime() : null;
          const end = bar.end ? new Date(bar.end).getTime() : start;
          if (start == null) return null;
          const clipStart = Math.max(start, winFromMs);
          const clipEnd = Math.max(clipStart, Math.min(end || start, winToMs));
          if (clipEnd < winFromMs || clipStart > winToMs) return null;
          const leftPct = ((clipStart - winFromMs) / spanMs) * 100;
          const widthPct = Math.max(1.2, ((clipEnd - clipStart) / spanMs) * 100);
          // Stagger to avoid overlaps
          const topPx = 4 + (i % 3) * 11;
          return (
            <BarChip
              key={`${bar.kind}-${bar.id || i}`}
              bar={bar}
              leftPct={leftPct}
              widthPct={widthPct}
              topPx={topPx}
            />
          );
        })}
      </div>
    </div>
  );
}

function BarChip({ bar, leftPct, widthPct, topPx }) {
  const node = (
    <div
      className="absolute h-[10px] rounded-sm flex items-center justify-start overflow-hidden text-[9px] px-1 cursor-pointer hover:ring-1 hover:ring-white/40 transition-all"
      style={{
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        top: topPx,
        background: bar.color || "#1FB6C1",
        color: "#0a0b0e",
      }}
      title={`${bar.label || bar.kind}${bar.subtitle ? ` · ${bar.subtitle}` : ""}${bar.status ? ` (${bar.status})` : ""} — ${bar.start?.slice(0, 10)}`}
      data-testid={`gantt-bar-${bar.kind}-${bar.id || ""}`}
    >
      <span className="truncate font-semibold">{bar.label}</span>
    </div>
  );
  return bar.link_url ? <Link to={bar.link_url}>{node}</Link> : node;
}
